import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Minus, Users, Coins, ScanLine, FileText } from "lucide-react";
import { BETA_MODE } from "@/lib/betaMode";

/**
 * AdminMetrics — KPI strip on /admin
 *
 * Four cards aimed at decisions Lovable Cloud cannot answer for us:
 *  • Cohort retention (last 30d signups → active in last 7d)
 *  • MRR per tier (price × active users; 0 in beta — placeholder until Stripe)
 *  • Invoices this month vs last month (with trend %)
 *  • Top 10 OCR consumers this month (cost driver — Gemini calls)
 *
 * One server call (`admin-users` action=metrics) returns everything to keep
 * the page snappy and avoid per-card waterfalls.
 */

type MetricsResponse = {
  cohort_retention: {
    cohort_size: number;
    active_last_7d: number;
    retention_pct: number;
  };
  mrr: {
    total_ron: number;
    per_tier: Array<{
      tier_id: string;
      tier_name: string;
      tier_slug: string;
      price_ron: number;
      user_count: number;
      mrr_ron: number;
    }>;
  };
  top_ocr_consumers: Array<{
    user_id: string;
    email: string | null;
    ocr_count: number;
  }>;
  invoices_trend: {
    this_month: number;
    prev_month: number;
    /** null when prev_month = 0 (no baseline → can't compute %) */
    trend_pct: number | null;
  };
};

const fmtRon = (v: number) =>
  new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON", maximumFractionDigits: 0 }).format(v);

const AdminMetrics = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<MetricsResponse>("admin-users", {
        body: { action: "metrics" },
      });
      if (error) throw error;
      return data!;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 rounded-xl border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }
  if (!data) return null;

  const { cohort_retention: cr, mrr, top_ocr_consumers: top, invoices_trend: inv } = data;

  // Trend visual: up/down/flat with semantic colors
  const trendIcon =
    inv.trend_pct === null ? Minus : inv.trend_pct > 0 ? TrendingUp : inv.trend_pct < 0 ? TrendingDown : Minus;
  const TrendIcon = trendIcon;
  const trendColor =
    inv.trend_pct === null
      ? "text-muted-foreground"
      : inv.trend_pct > 0
        ? "text-success"
        : inv.trend_pct < 0
          ? "text-destructive"
          : "text-muted-foreground";

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Cohort retention */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" /> Retenție cohortă 30z
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="text-3xl font-bold font-mono">{cr.retention_pct}%</div>
          <p className="text-xs text-muted-foreground">
            {cr.active_last_7d} din {cr.cohort_size} useri înscriși în ultimele 30 zile
            au fost activi în ultimele 7 zile.
          </p>
          {cr.cohort_size === 0 && (
            <p className="text-xs italic text-muted-foreground">Niciun user nou în ultimele 30 zile.</p>
          )}
        </CardContent>
      </Card>

      {/* MRR */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <Coins className="h-4 w-4" /> MRR estimat
            {BETA_MODE && (
              <Badge variant="outline" className="ml-1 text-[10px] py-0 h-4">
                Beta
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="text-3xl font-bold font-mono">{fmtRon(mrr.total_ron)}</div>
          <div className="text-xs text-muted-foreground space-y-0.5">
            {mrr.per_tier.length === 0 ? (
              <p className="italic">Nu există tier-uri active.</p>
            ) : (
              mrr.per_tier.map((t) => (
                <div key={t.tier_id} className="flex justify-between">
                  <span>
                    {t.tier_name} · {t.user_count} useri × {fmtRon(t.price_ron)}
                  </span>
                  <span className="font-mono">{fmtRon(t.mrr_ron)}</span>
                </div>
              ))
            )}
          </div>
          {BETA_MODE && (
            <p className="text-xs italic text-muted-foreground pt-1">
              În Beta plățile sunt off — toate prețurile rămân 0 până la lansare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invoices this month vs last month */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <FileText className="h-4 w-4" /> Facturi luna curentă
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold font-mono">{inv.this_month.toLocaleString("ro-RO")}</div>
            <div className={`flex items-center gap-0.5 text-sm font-medium ${trendColor}`}>
              <TrendIcon className="h-4 w-4" />
              {inv.trend_pct === null ? "—" : `${inv.trend_pct > 0 ? "+" : ""}${inv.trend_pct}%`}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Luna trecută: <span className="font-mono">{inv.prev_month.toLocaleString("ro-RO")}</span>
          </p>
          {inv.trend_pct === null && inv.this_month === 0 && (
            <p className="text-xs italic text-muted-foreground">Nici o factură încă.</p>
          )}
          {inv.trend_pct === null && inv.this_month > 0 && (
            <p className="text-xs italic text-muted-foreground">
              Nici o bază de comparație (0 luna trecută).
            </p>
          )}
        </CardContent>
      </Card>

      {/* Top OCR consumers */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <ScanLine className="h-4 w-4" /> Top 10 OCR (luna curentă)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {top.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">Nici o scanare luna asta.</p>
          ) : (
            <ol className="space-y-1 text-xs">
              {top.map((u, i) => (
                <li key={u.user_id} className="flex justify-between gap-2">
                  <span className="truncate">
                    <span className="text-muted-foreground mr-1">{i + 1}.</span>
                    {u.email ?? u.user_id.slice(0, 8)}
                  </span>
                  <span className="font-mono shrink-0 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-primary" />
                    {u.ocr_count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMetrics;
